import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";

const travelRelated = [
  {
    title: "SAINT® TMS Locations",
    href: "/learn/saint-tms-locations",
    description: "Verified SAINT® providers by state and how to confirm one",
  },
  {
    title: "SAINT® TMS Cost",
    href: "/learn/saint-tms-cost",
    description: "Pricing, insurance and financing for SAINT®",
  },
  {
    title: "What Is a Verified SAINT® Provider?",
    href: "/learn/what-is-a-verified-saint-provider",
    description: "What verification means and why it matters",
  },
  {
    title: "SAINT® TMS Near New York",
    href: "/locations/saint-tms-new-york",
    description: "Traveling from the New York area to Coral Springs",
  },
  {
    title: "SAINT® TMS Near Tampa",
    href: "/locations/saint-tms-tampa",
    description: "Traveling from the Tampa Bay area to Coral Springs",
  },
];

const faqs: LearnFAQ[] = [
  {
    question: "Do Florida residents need to travel out of state for 5-day TMS?",
    answer:
      "No. Neuro Wellness in Coral Springs is a verified SAINT\u00ae provider listed on Magnus Medical's provider locator, so Floridians can get FDA-cleared SAINT\u00ae in-state, without flights or a week of hotel bills across the country.",
  },
  {
    question: "Can I fly in for SAINT?",
    answer:
      "Yes. Many patients fly to South Florida for SAINT® because the protocol takes five consecutive days rather than six weeks. Fort Lauderdale-Hollywood International (FLL) is the closest airport, about 35–45 minutes from the clinic. We recommend arriving the day before treatment begins and departing after Day 5. A free phone or video consultation before you book travel confirms whether SAINT® is a fit.",
  },
  {
    question: "How long do I need to stay?",
    answer:
      "Plan for about seven days: arrive the day before treatment (Day 0), complete treatment Days 1 through 5, and depart on Day 6. The fMRI mapping scan is typically done the day before or the morning of Day 1. If your insurance requires prior authorization, that is handled before you travel so your week is not delayed.",
  },
  {
    question: "Can a family member come with me?",
    answer:
      "Yes. A spouse, partner, family member, or friend can travel with you and stay at the same hotel. Because SAINT® uses no anesthesia or sedation, a companion is optional — you can drive yourself — but many patients value having someone nearby for support during the week. There is no medical caregiving requirement.",
  },
  {
    question: "Will my insurance work out of state?",
    answer:
      "It may. BlueCard (Blue Cross Blue Shield out-of-state), FEHB, and Medicare may cover SAINT® depending on your plan and medical necessity. Coverage varies by plan; we verify your benefits for free before you travel so there are no surprises. SAINT® at NWCOA starts at $25,000 for self-pay, and financing is available on a case-by-case basis.",
  },
  {
    question: "What happens after I go home?",
    answer:
      "After the treatment week, you return home for follow-up. We schedule check-ins by phone or video at set intervals to track your response. If you have a local psychiatrist or therapist, we coordinate with them and share notes so your care continues seamlessly at home. If SAINT® does not fully resolve your symptoms, we discuss next steps — which may include SPRAVATO®, ketamine, or medication management, all available at our clinic.",
  },
];

export const pageTravelForSaint = makeArticle({
  slug: "travel-for-saint-tms",
  title:
    "Traveling for SAINT® TMS: Out-of-State Patient Program | Coral Springs, FL",
  description:
    "Coming to South Florida for SAINT®? How the 5-day week works for travelers: airports, lodging, who can come with you, insurance from out of state, and follow-up at home.",
  canonical: "/learn/travel-for-saint-tms",
  h1: "Traveling for SAINT® TMS: Your Week in Coral Springs",
  breadcrumbLabel: "Traveling for SAINT®",
  shortAnswer:
    "SAINT® delivers 50 brain-stimulation sessions over five consecutive days, which makes traveling for treatment practical — you stay in Coral Springs for one week instead of commuting for six. Neuro Wellness TMS Centers of America, a Verified SAINT® Provider in Coral Springs, Florida, sees patients from across the country. A free phone or video consultation before you book travel confirms whether SAINT® is a fit for you.",
  keyTakeaways: [
    "SAINT® is 5 consecutive days, making a single travel week practical.",
    "Fort Lauderdale-Hollywood International (FLL) is the closest airport.",
    "A free consultation by phone or video happens before you book travel.",
    "A companion can come with you; no medical driver is required.",
    "Out-of-state insurance (BlueCard, FEHB, Medicare) may apply; we verify for free.",
    "Follow-up after the week is by phone or video, coordinated with your local doctor.",
  ],
  sections: [
    {
      heading: "Before you fly anywhere for accelerated TMS",
      paragraphs: [
        "Before you book flights or pay a deposit at any 5-day TMS clinic, protect yourself by asking these five questions:",
      ],
      list: [
        "1. Is this the FDA-cleared SAINT\u00ae protocol from a provider listed on Magnus Medical's provider locator?",
        "2. Are the brain MRI scan, psychiatric consultation, all 50 sessions, and follow-up included in your quote?",
        "3. Is the targeting software FDA-cleared for personalized fMRI targeting?",
        "4. Was your exact protocol tested in the SAINT\u00ae clinical trials?",
        "5. Can my insurance or Medicare be billed for a benefits check or prior authorization?",
      ],
      paragraphsAfter: [
        "Read our [[5-question clinic checklist|/learn/what-is-a-verified-saint-provider]] before you travel.",
      ],
    },
    {
      heading: "Quick Answer: What the Travel Week Looks Like",
      paragraphs: [
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) compresses what would be six weeks of standard TMS into five consecutive days. Each treatment day includes ten 10-minute stimulation sessions with 50-minute breaks in between, so a full day runs roughly 10 hours, Monday through Friday. Because the entire course fits into one week, patients from across the country travel to Neuro Wellness TMS Centers of America in Coral Springs, Florida — a Verified SAINT® Provider — without taking six weeks away from work and family.",
        "Before you book a flight or hotel, you start with a free consultation by phone or video. That consultation confirms whether SAINT® is clinically appropriate for you, reviews your treatment history, and starts the insurance verification process. Only after that do you schedule travel. The week itself involves an fMRI mapping scan (either the day before or the morning of Day 1), five days of treatment, and a follow-up plan for when you return home.",
      ],
    },
    {
      heading: "Before You Travel: Candidacy, MRI, and Benefits",
      paragraphs: [
        "The first step is a free consultation with our clinical team, conducted by phone or video so you do not need to travel for it. We review your diagnosis, treatment history, and whether SAINT® is a fit. SAINT® is FDA-cleared for adults with major depressive disorder (MDD) whose depression has not improved enough on prior antidepressant medication. NWCOA treats adults 22 and older.",
        "If SAINT® is appropriate, we schedule the fMRI (functional MRI) mapping scan that personalizes your treatment target. This scan may be done the day before treatment begins or the morning of Day 1, depending on scheduling. We also begin insurance verification at this stage — we contact your plan to confirm benefits, prior authorization requirements, and any out-of-pocket costs before you book travel.",
      ],
      list: [
        "Free phone or video consultation to confirm candidacy",
        "Insurance and benefits verification before you book travel",
        "fMRI mapping scan scheduled for the day before or morning of Day 1",
        "Pack comfortable clothing, your medication list, and any imaging records",
        "Bring a companion if you'd like support during the week",
      ],
    },
    {
      heading: "Getting to Coral Springs: Airports and Ground Transport",
      paragraphs: [
        "Neuro Wellness is located at 9734 W Sample Rd, Coral Springs, FL 33065, in northern Broward County. Three major South Florida airports serve the area:",
      ],
      list: [
        "Fort Lauderdale-Hollywood International (FLL) — closest, about 35–45 minutes by car",
        "Palm Beach International (PBI) — about 40–50 minutes by car",
        "Miami International (MIA) — about 60 minutes by car",
      ],
      paragraphsAfter: [
        "A rental car is the most convenient option for the week, and free parking is available at the clinic. Rideshare services (Uber, Lyft) are widely available throughout South Florida if you prefer not to drive. Because SAINT® uses no anesthesia or sedation, you can drive yourself to and from the clinic each day.",
      ],
    },
    {
      heading: "Where to Stay: Hotels Near the Clinic",
      paragraphs: [
        "Several hotels and extended-stay properties are within a short drive of the clinic in Coral Springs and the neighboring communities of Parkland, Margate, and Coconut Creek. For a treatment week, look for lodging with:",
      ],
      list: [
        "A kitchen or kitchenette (extended-stay properties) for the week",
        "Proximity to the clinic — ideally under a 15-minute drive",
        "Reliable Wi-Fi if you plan to work remotely between sessions",
        "Comfortable common space for a companion who travels with you",
      ],
      paragraphsAfter: [
        "We can share a list of nearby hotels and extended-stay properties once your treatment week is scheduled. Hotel rates and exact distances should be confirmed directly with the property, as availability and pricing vary by season. Arrangements are made directly between you and the hotel.",
      ],
    },
    {
      heading: "Who Can Come With You, and What a Support Person Does",
      paragraphs: [
        "A spouse, partner, family member, or friend can travel with you and stay at the same hotel. Because SAINT® uses no anesthesia or sedation, a companion is optional — you can drive yourself and do not need a medical caregiver. However, many patients value having someone nearby for emotional support during an intense week.",
        "A support person can wait at the clinic, return to the hotel between sessions, or explore the Coral Springs area. Each treatment day takes most of the day (ten sessions with breaks), so companions should plan for a flexible schedule. There is no medical caregiving duty, but having someone to talk to between sessions and share meals with can make the week easier.",
      ],
    },
    {
      heading: "The 5 Days Hour by Hour, and the Day After",
      paragraphs: [
        "Each treatment day follows the same rhythm: you arrive at the clinic, receive ten 10-minute stimulation sessions spaced 50 minutes apart, and leave in the late afternoon. The 50-minute breaks are yours — many patients work on a laptop, read, or rest. Wi-Fi is available at the clinic.",
        "Day 1 may begin with the fMRI mapping scan if it was not done the day before. By Day 5, the full 50-session course is complete. On Day 6 (or Day 5 evening if flights allow), you travel home. There is no post-treatment recovery period — SAINT® uses no sedation, so you can fly the same day your treatment ends.",
      ],
    },
    {
      heading: "Insurance and Payment From Out of State",
      paragraphs: [
        "If you live outside Florida, your insurance may still cover SAINT®. Out-of-state Blue Cross Blue Shield members can use BlueCard, which allows you to access in-network benefits at participating providers in Florida. Federal Employee Health Benefits (FEHB) plans and Medicare may also apply, subject to plan terms and medical necessity. Coverage varies by plan; we verify your benefits for free before you travel so you understand your costs upfront.",
        "For self-pay patients, SAINT® at NWCOA starts at $25,000. This includes the fMRI mapping scan, all 50 stimulation sessions, physician oversight, and the treatment-week coordination. Financing is available on a case-by-case basis and discussed during your consultation. We never quote a lower or exact final price without reviewing your individual situation.",
      ],
    },
    {
      heading: "Going Home: Follow-Up and Coordination With Your Doctor",
      paragraphs: [
        "After the treatment week, you return home and we continue your care remotely. We schedule follow-up appointments by phone or video at set intervals to track your response and adjust any medication. If you have a local psychiatrist, therapist, or primary care provider, we coordinate with them and share treatment notes so your care continues seamlessly at home.",
        "If SAINT® does not fully resolve your symptoms, we discuss next steps — which may include SPRAVATO® (esketamine), IV ketamine, medication management, or psychotherapy, all available at our clinic. You are not sent elsewhere. Individual results may vary; 79% of participants in the 2022 randomized trial reached remission (Cole et al., Am J Psychiatry 2022;179(2):132–141), but not everyone responds, and follow-up is part of the plan.",
      ],
    },
    {
      heading: "If You Already Live in Florida",
      paragraphs: [
        "If you live in Florida, you do not need to leave the state for SAINT®. Neuro Wellness TMS Centers of America in Coral Springs is a Verified SAINT® Provider serving patients from Broward, Miami-Dade, and Palm Beach counties and beyond. Patients within driving distance can commute daily; patients from other parts of Florida (Tampa, Orlando, Jacksonville) can travel to Coral Springs for the week. The same consultation, MRI, and treatment-week process applies whether you drive in or fly in.",
      ],
    },
    {
      heading: "State-by-State Travel Pages",
      paragraphs: [
        "We've created travel guides for patients coming from specific regions. If you're traveling from one of these areas, see the dedicated page for drive times, lodging, and local details:",
      ],
      links: [
        {
          to: "/locations/saint-tms-new-york",
          text: "SAINT® TMS near New York",
        },
        {
          to: "/locations/saint-tms-tampa",
          text: "SAINT® TMS near Tampa",
        },
        {
          to: "/locations/saint-tms-miami",
          text: "SAINT® TMS near Miami",
        },
        {
          to: "/locations/saint-tms-coral-gables",
          text: "SAINT® TMS near Coral Gables",
        },
      ],
    },
    {
      heading: "Travel Steps at a Glance",
      table: {
        headers: ["Travel step", "When", "Who arranges it", "Tips"],
        rows: [
          [
            "Free consultation",
            "Before booking travel",
            "You + our team (phone/video)",
            "Have your medication history ready",
          ],
          [
            "Insurance verification",
            "Before booking travel",
            "Our team (we call your plan)",
            "We confirm benefits and prior auth",
          ],
          [
            "Book flights",
            "After consultation",
            "You",
            "FLL is closest; arrive Day 0",
          ],
          [
            "Book hotel",
            "After consultation",
            "You (we share a list)",
            "Extended-stay with kitchen preferred",
          ],
          [
            "fMRI mapping scan",
            "Day 0 or Day 1 morning",
            "Our clinic",
            "Personalizes your treatment target",
          ],
          [
            "Treatment week",
            "Days 1–5 (Mon–Fri)",
            "Our clinic",
            "10 sessions/day, 50-min breaks",
          ],
          [
            "Follow-up",
            "After you return home",
            "Our team (phone/video)",
            "Coordinated with your local doctor",
          ],
        ],
      },
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, Stimpson KH, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. American Journal of Psychiatry, 2022;179(2):132–141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101499",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — SAINT Neuromodulation System (Magnus Medical). FDA, 2021.",
      url: "https://www.fda.gov/medical-devices/510k-clearances",
    },
  ],
  about: {
    "@type": "MedicalTherapy",
    name: "SAINT® Accelerated TMS",
    description:
      "FDA-cleared, fMRI-guided 5-day accelerated transcranial magnetic stimulation for adults with major depressive disorder.",
  },
  relatedLinks: travelRelated,
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
